<template>

  <div>

    <div :id="_uid" class="ui large modal" :class="modalSelector">
      <div class="ui header">
        {{ isCreating ? "Novo Título a Pagar" : "Alterar Título a Pagar" }}
      </div>
      <div class="content">
        <div class="ui form" :class="{ loading }">
          <div class="fields">
            <div class="six wide field">
              <label class="form-label">Fornecedor <span class="red text">*</span></label>
              <supplier-picker ref="supplierPicker" @onClear="onClearSupplier" @onPick="onPickSupplier" />
            </div>
            <div class="six wide field">
              <label class="form-label">Natureza <span class="red text">*</span></label>
              <select-dropdown ref="natureDropdown" @onChange="onChangeNature" />
            </div>
            <div class="four wide field">
              <label class="form-label">Tipo <span class="red text">*</span></label>
              <select-dropdown ref="typeDropdown" @onChange="onChangeType" />
            </div>
          </div>
          <div class="fields">      
            <div class="four wide field">
              <label class="form-label">Emissão <span class="red text">*</span></label>
              <date-picker ref="datePicker" :autoInit="false" @onChange="onChangeDate" />
            </div>     
            <div class="four wide field">
              <label class="form-label">Vencimento <span class="red text">*</span></label>
              <date-picker ref="dueDatePicker" :autoInit="false" @onChange="onChangeDueDate" />
            </div>                                     
            <div class="four wide field">
              <label class="form-label">Venc. Real <span class="red text">*</span></label>
              <date-picker ref="realDueDatePicker" :autoInit="false" @onChange="onChangeRealDueDate" />
            </div>  
            <div class="four wide field">
              <label class="form-label">Valor <span class="red text">*</span></label>
              <input class="money" :value="payableValue" @blur="payableValue = $event.target.value">
            </div>
          </div>
          <div class="fields">
            <div class="sixteen wide field">
              <label class="form-label">Observações</label>
              <input type="text" v-model="payable.comments" />
            </div>
          </div>
        </div>
      </div>
      <div class="actions">
        <button class="ui cancel basic button">
          <i class="times icon"></i>
          Cancelar
        </button>      
        <button v-if="isUpdating" class="ui basic red button" 
          :class="{ loading: checkingRemoval || removing, disabled: saving }" @click="onRemove()">
          <i class="trash icon"></i> Excluir
        </button>
        <button class="ui blue button" :class="{ loading: saving, disabled: !canConfirm }" @click="onConfirm()">
          <i class="checkmark icon"></i>
          {{ isCreating ? "Confirmar" : "Atualizar" }}
        </button>
      </div>
    </div>

    <can-not-remove-modal ref="canNotRemoveModal" />

    <yes-no-modal ref="confirmRemovalModal" :message="`Confirma exclusão do título a pagar ${payable.id} ?`" 
      :danger="true" @onYes="onConfirmRemoval()"/>

  </div>
  
</template>

<script>

  import modalDialog from "@/mixins/modal-dialog"
  import PayableService from "@/services/payable-service"
  import FinancialNatureService from "@/services/financial-nature-service"
  import { moment, Input } from "@/utils"
  import { SupplierPicker, DatePicker, SelectDropdown } from "@/components"

  export default {

    name: "PayableFormModal",

    mixins: [modalDialog],

    data() {
      return {
        payable: {},
        service: new PayableService(),
      }
    },

    components: {
      SupplierPicker,
      DatePicker,
      SelectDropdown,
    },

    mounted() {

      Input.maskCurrency()

      this.loadNatures()
      
      this.$refs.typeDropdown.setValues(PayableService.getTypeList())
    },

    computed: {

      payableValue: {
        get() {
          return Input.currencyToString(this.payable.value)
        },
        set(value) {
          this.payable.value = Input.stringToCurrency(value)    
          this.refreshData()   
        },
      },

      canConfirm() {
        return (
          this.payable.supplierId &&
          this.payable.natureId &&
          this.payable.type &&
          this.payable.date &&
          this.payable.dueDate &&
          this.payable.realDueDate &&
          this.payable.value
        )
      },
    },    
    
    methods: {

      async loadNatures() {

        this.loading = true

        try {

          const natures = await new FinancialNatureService().findAll({
            attributes: ["id", "type", "description"],
            order: "description",
          })

          const expenseNatures = natures.filter((nature) => nature.type == "expense")

          this.$refs.natureDropdown.setValues(expenseNatures)
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.loading = false
        }
      },

      load(payable) {

        this.loading = true

        this.$refs.supplierPicker.clear()
        this.$refs.natureDropdown.reset()
        this.$refs.typeDropdown.reset()
        this.$refs.datePicker.clearDate()
        this.$refs.dueDatePicker.clearDate()
        this.$refs.realDueDatePicker.clearDate()

        this.payable = {
          type: "normal",
          date: new Date(),
        }                

        if (!payable)
          return this.show()
        
        this.payable = this.$cloneObject(payable)
        
        this.payable.date = moment(payable.date).toDate()
        this.payable.dueDate = moment(payable.dueDate).toDate()
        this.payable.realDueDate = moment(payable.realDueDate).toDate()        

        this.show()
      },

      onVisible() {
        this.emitEvent = null
        this.initData()
        this.loading = false
      },      

      initData() {

        if (this.payable.supplier) {  
          this.payable.supplierId = this.payable.supplier.id
          this.$refs.supplierPicker.setSelected(this.payable.supplier)
        }  
        
        this.$refs.natureDropdown.setSelected(this.payable.natureId)
        this.$refs.typeDropdown.setSelected(this.payable.type)        
                
        this.$refs.datePicker.initCalendar({ force: true })
        this.$refs.dueDatePicker.initCalendar({ force: true })
        this.$refs.realDueDatePicker.initCalendar({ force: true })
        
        this.$refs.datePicker.setDate(this.payable.date)        

        if (this.payable.dueDate)
          this.$refs.dueDatePicker.setDate(this.payable.dueDate)

        if (this.payable.realDueDate)  
          this.$refs.realDueDatePicker.setDate(this.payable.realDueDate)
      },

      onPickSupplier(supplier) {
        this.payable.supplierId = supplier.id
        this.payable.supplier = supplier
        this.refreshData()
      },

      onClearSupplier() {
        this.payable.supplierId = null
        this.refreshData()
      },

      onChangeNature(id) {
        this.payable.natureId = id ? parseInt(id) : null
        this.refreshData()
      },

      onChangeType(id) {
        this.payable.type = id
        this.refreshData()
      },

      onChangeDate(date) {
        this.payable.date = date
        this.refreshData()          
      },

      onChangeDueDate(date) {
        
        this.payable.dueDate = date

        if (!this.loading)
          this.$refs.realDueDatePicker.setDate(moment.getNextBusinessDay(date))

        this.refreshData()          
      },

      onChangeRealDueDate(date) {
        this.payable.realDueDate = date
        this.refreshData()          
      },

      refreshData() {
        this.payable = this.$cloneObject(this.payable)
      },

      async onConfirm() {

        if (this.saving)
          return

        this.saving = true
        
        try {

          this.payable.date = moment(this.payable.date).format("YYYY-MM-DD")
          this.payable.dueDate = moment(this.payable.dueDate).format("YYYY-MM-DD")
          this.payable.realDueDate = moment(this.payable.realDueDate).format("YYYY-MM-DD")

          if (this.isCreating) {

            const newPayable = await this.service.create(this.payable)

            this.payable.id = newPayable.id

          } else {            

            await this.service.update(this.payable)            
          }

          this.$reloadDataTables(["payables"])

          this.emit("onSave", this.payable)

          this.$msg.success(this.isCreating ? "Título criado com sucesso" : "Título alterado com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.saving = false
        }
      },

      async onRemove() {

        if (this.payable.invoiceId)
          return this.$msg.warn("Esse título não pode ser excluído porque foi gerado a partir de um fechamento")

        this.checkingRemoval = true

        try {

          const data = await this.service.canRemove(this.payable)

          if (data.canRemove)  {
            this.$refs.confirmRemovalModal.show()              
          } else {
            this.$refs.canNotRemoveModal.show(data.entities)            
          }

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.checkingRemoval = false
        }        
      },

      async onConfirmRemoval() {

        if (this.removing)
          return

        this.removing = true

        try {

          await this.service.remove(this.payable) 

          this.$reloadDataTables(["payables"])

          this.emit("onRemove", this.payable)

          this.$msg.success("Título excluído com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.removing = false
        }
      },
    },
  }

</script>